import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { next, updateWhereaboutsIconPack } from '../../../store/onboarding.duck';
import { Column } from '@shared/ui/atoms/StructuralLayout';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import { selectCurrentUser } from "@shared/store/auth.duck";
import { IconPack } from "@shared/models/company.models";
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export default function OnboardingIcons() {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const user = useSelector(selectCurrentUser);
  const [loading, setLoading] = useState(false);

  const basic = user?.companyEntity.iconPack === IconPack.BASIC;
  const standard = user?.companyEntity.iconPack === IconPack.STANDARD;

  const onSelect = async (iconPack: IconPack) => {
    if (iconPack === user?.companyEntity.iconPack) return;

    try {
      setLoading(true);
      dispatch(updateWhereaboutsIconPack(iconPack));
      await new Promise((resolve: any) => setTimeout(() => resolve(), 150));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="onboarding">
      <Column className="onboarding__content">
        <HeadlineMedium style={{marginBottom: 12}}>{t('onboarding.icons-title')}</HeadlineMedium>
        <BodyLarge style={{marginBottom: 48}}>{t('onboarding.icons-description')}</BodyLarge>

        <IconRow>
          <IconWrapper selected={standard} onClick={() => onSelect(IconPack.STANDARD)}>
            {standard && <SelectedIcon icon={IconTypes.Tick} circle={true} size={'small'} color={'blue'} />}
            <Image src="/assets/icons/standard/House.svg" alt="detailed icons" aria-hidden={true} />
            <BodySmall weight={600}>{t('onboarding.icons-detailed')}</BodySmall>
          </IconWrapper>
          <IconWrapper selected={basic} onClick={() => onSelect(IconPack.BASIC)}>
            {basic && <SelectedIcon icon={IconTypes.Tick} circle={true} size={'small'} color={'blue'} />}
            <Image src="/assets/icons/basic/House-Available.svg" alt="simple icons" aria-hidden={true} />
            <BodySmall weight={600}>{t('onboarding.icons-simple')}</BodySmall>
          </IconWrapper>
        </IconRow>

        <PrimaryButton dataTest="onboarding-custom-whereabouts-next"
                       size={ButtonSize.Large}
                       text="button.got-it"
                       disabled={loading}
                       click={() => dispatch(next())}
                       fullWidth={true}/>
      </Column>
    </div>
  )
}


const IconRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 48px;
`

const SelectedIcon = styled(Icon)<any>`
  position: absolute;
  top: 8px;
  right: 8px;
`

const IconWrapper = styled.div<any>`
  position: relative;
  width: 150px;
  max-width: 50%;
  border-radius: 12px;
  border: ${props => props.selected ? `2px solid ${Colours.blue}` : `1px solid ${Colours.lightGrey}`};
  background: #FFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 16px 24px 16px;
  &:hover {
    border: 2px solid ${Colours.blue};
  }
  &:first-child {
    margin-right: 8px;
  }
  &:last-child {
    margin-left: 8px;
  }
`

const Image = styled.img`
  margin-bottom: 6px;
`
